import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CollectionForm } from './CollectionForm';
import { ItemCollectionFormValues } from './schema';
import {
  getItemCollection,
  createItemCollection,
  updateItemCollection,
  addItemToCollection,
  removeItemFromCollection,
} from '@/app/services/itemCollectionService';
import { ItemCollection } from '@/app/types/itemCollection';
import { useOrganization } from '@/app/hooks/useOrganization';

const ItemCollectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const isEditMode = !!id;

  // Fetch existing collection if in edit mode
  const {
    data: collection,
    isLoading,
    isError,
    error,
  } = useQuery<ItemCollection>({
    queryKey: ['item-collection', id],
    queryFn: () => getItemCollection(Number(id), true),
    enabled: isEditMode && !!id,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ItemCollectionFormValues) => {
      // Create collection
      const collectionData = {
        name: data.name,
        slug: data.slug,
        description: data.description,
        is_active: data.is_active,
      };
      const newCollection = await createItemCollection(collectionData);

      // Add items to collection
      for (const item of data.items) {
        await addItemToCollection(newCollection.id, {
          item_id: item.item_id,
          sort_order: item.sort_order,
        });
      }

      return newCollection;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['item-collections'] });
      toast.success('Collection created successfully');
      navigate('/items/collections');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create collection: ${error.message}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ItemCollectionFormValues) => {
      if (!id) throw new Error('Collection ID is required');
      
      const collectionId = Number(id);
      
      // Update collection details
      const collectionData = {
        name: data.name,
        slug: data.slug,
        description: data.description,
        is_active: data.is_active,
      };
      const updatedCollection = await updateItemCollection(collectionId, collectionData);

      // Get current items
      const currentCollection = await getItemCollection(collectionId, true);
      const currentItemIds = new Set(currentCollection.items?.map(item => item.id) || []);
      const newItemIds = new Set(data.items.map(item => item.item_id));

      // Remove items that are no longer in the collection
      for (const itemId of currentItemIds) {
        if (!newItemIds.has(itemId)) {
          await removeItemFromCollection(collectionId, itemId);
        }
      }

      // Add new items or update existing ones
      for (const item of data.items) {
        if (!currentItemIds.has(item.item_id)) {
          await addItemToCollection(collectionId, {
            item_id: item.item_id,
            sort_order: item.sort_order,
          });
        }
      }

      return updatedCollection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-collections'] });
      queryClient.invalidateQueries({ queryKey: ['item-collection', id] });
      toast.success('Collection updated successfully');
      navigate('/items/collections');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update collection: ${error.message}`);
    },
  });

  const handleSubmit = (values: ItemCollectionFormValues) => {
    if (isEditMode) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto py-6">
        <Alert variant="destructive">
          <AlertDescription>
            Error loading collection: {(error as Error)?.message || 'An unknown error occurred'}
          </AlertDescription>
        </Alert>
        <Button
          className="mt-4"
          variant="outline"
          onClick={() => navigate('/items/collections')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Collections
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/items/collections')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditMode ? 'Edit Collection' : 'Create Collection'}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode 
                ? `Editing "${collection?.name}"` 
                : 'Create a new item collection'}
            </p>
          </div>
        </div>
      </div>

      <CollectionForm
        initialData={collection}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        disabled={isSubmitting}
      />
    </div>
  );
};

export default ItemCollectionDetailPage;